import { useState } from "react";
import { Language } from "utils";
import { intl } from "LocaleWrapper";
import { useMutation } from "@apollo/client";
import { gql } from "__generated__/gql";
import CharterSelect, { CharterSelectOption } from "view/charters/components/selects/CharterSelect";
import { Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { FormGroup, Button as CCKButton, showSnackbar } from "@cofano/cck";
import FormField from "components/cck/FormField";
import { LocationLink, ShipLink, VoyageLink } from "utils/LinkUtils";
import DateRangePicker from "components/cck/DateRangePicker";
import { BulkPlanningReplan, BulkPlanningReplanDialog, CargoActionsFromQuery } from "view/planning/bulk/BulkPlanning";
import { toCargoActionTimesInput } from "view/planning/bulk/BulkPlanningUtils";
import { DateUtils } from "utils/DateUtils";
import DatePicker from "components/cck/DatePicker";
import { UpdateCargoActionsTimesDocument } from "__generated__/view/cargos/graphql/cargo.mutations.generated";
import { CharterSubtype, CharterType } from "__generated__/enums";

const UnplannedCargoReplanDialog = ({ voyage, actions, newShip, charters, callback, onClose }: {
	callback: () => void;
	onClose: () => void;
} & Pick<BulkPlanningReplanDialog, "voyage" | "actions"> & Partial<Pick<BulkPlanningReplan, "newShip" | "charters">>) => {

	const [ visible, setVisible ] = useState(true);

	const [ updateCargoActionsTimes, { loading: updateCargoActionsTimesLoading } ] = useMutation(UpdateCargoActionsTimesDocument);
	const [ changeVoyageShipAndCharter, { loading: changeVoyageShipAndCharterLoading } ] = useMutation(VOYAGE_MUTATION);
	const initialCharter = (charters?.find(c => c.id === voyage.purchaseCharters?.[0]?.id) ? voyage.purchaseCharters?.[0] : undefined) as CharterSelectOption | undefined;

	const { control, handleSubmit, formState, watch, setValue } = useForm({
		defaultValues: {
			actions: actions,
			charter: initialCharter,
		},
	});

	const { fields } = useFieldArray({
		control,
		name: "actions",
		keyName: "generatedId",
	});

	const loading = updateCargoActionsTimesLoading || changeVoyageShipAndCharterLoading;
	const formValues = watch();

	const replanVoyage = (actions: CargoActionsFromQuery[]) => {
		updateCargoActionsTimes({
			variables: {
				input: actions.map(toCargoActionTimesInput),
			},
		}).then(() => {
			closeModal();
			showSnackbar(intl.formatMessage({ id: "SUCCESSFUL_SAVED" }), { variant: "success" });
			setTimeout(() => callback(), 300);
		});
	};

	const save = (data: typeof formValues) => {
		if (!newShip) {
			return;
		}
		changeVoyageShipAndCharter({
			variables: {
				voyageId: voyage.id,
				shipId: newShip.id,
				charterInput: {
					...data.charter,
				},
			},
		}).then(() => {
			replanVoyage(data.actions);
		});
	};

	const closeModal = () => {
		setVisible(false);
		setTimeout(() => {
			onClose();
		}, 250);
	};

	return (
		<Dialog open={visible} onClose={closeModal} fullWidth>
			<DialogTitle>
				<span>Replanning voyage: <VoyageLink voyage={voyage} /> on ship: <ShipLink ship={newShip} /></span>
			</DialogTitle>

			<form onSubmit={handleSubmit(form => save(form))}>
				<DialogContent dividers>
					<FormGroup label={Language("CALLS")} overflow="hidden" required>
						{fields.map((item, index) => {
							return (
								<div key={item.generatedId} style={{ margin: "10px 0" }}>
									<span><LocationLink location={formValues.actions[index]?.location} /></span>

									<FormField errors={formState.errors} name={`actions.${index}.start`}>
										{actions[index]?.displayAsWindow ? (
											<Controller
												name={`actions.${index}.start`}
												control={control}
												rules={{ required: true }}
												render={({ field }) => (
													<DatePicker
														{...field}
														value={DateUtils.getTimestamp(field.value) ?? undefined}
														showTimeInput
													/>
												)}
											/>
										) : (
											<Controller
												name={`actions.${index}.start`}
												control={control}
												rules={{ required: true }}
												render={() => (
													<DateRangePicker
														startDate={DateUtils.getDateOrUndefined(formValues.actions[index]?.start)}
														endDate={DateUtils.getDateOrUndefined(formValues.actions[index]?.end)}
														onChange={(window) => {
															setValue(`actions.${index}.start`, DateUtils.getTimestamp(window[0]) ?? undefined);
															setValue(`actions.${index}.end`, DateUtils.getTimestamp(window[1]) ?? undefined);
														}}
														showTimeInput
													/>
												)}
											/>
										)}
									</FormField>
								</div>);
						})}
					</FormGroup>

					<FormGroup label={Language("CHARTER")} overflow="hidden" required>
						<FormField errors={formState.errors} name="charter">
							<Controller
								name="charter"
								control={control}
								rules={{ required: true }}
								render={({ field }) => (
									<CharterSelect
										search={{
											ship: newShip,
											charterType: CharterType.Purchase,
											charterSubtypes: [ CharterSubtype.Time ],
											creditors: [ newShip?.operator?.id, newShip?.owner?.id ],
											hasContract: true,
											start: DateUtils.getTimestamp(formValues.actions[0]?.start) ?? undefined,
											end: DateUtils.getTimestamp(formValues.actions.at(-1)?.end) ?? undefined,
										}}
										value={(initialCharter || field.value) as CharterSelectOption}
										onChange={(option) => field.onChange(option)}
										onCreate={(option) => field.onChange({ ...option, id: 0 })}
									/>
								)}
							/>
						</FormField>
					</FormGroup>
				</DialogContent>

				<DialogActions>
					<CCKButton type="submit" loading={loading}>{Language("SAVE")}</CCKButton>
					<CCKButton onClick={() => closeModal()} disabled={loading} variant="outlined">{Language("CANCEL")}</CCKButton>
				</DialogActions>
			</form>
		</Dialog>
	);
};

const VOYAGE_MUTATION = gql(`#graphql
    mutation changeVoyageShipAndCharter($voyageId: Long!, $shipId: Long!, $charterInput: CharterInput!) {
        changeVoyageShipAndCharter(voyageId: $voyageId, shipId: $shipId, charterInput: $charterInput)
    }
`);

export default UnplannedCargoReplanDialog;
